/**
 * Analytics Service - Mixpanel Integration
 * Tracks user behavior and business events for SalesQualifyd
 * 
 * Features:
 * - Event queueing for when Mixpanel hasn't loaded yet
 * - Initialization check with retry logic
 * - Automatic queue processing when Mixpanel becomes available
 */

// Event queue for when Mixpanel hasn't loaded yet
let eventQueue = [];
let isInitialized = false;
let initCheckAttempts = 0;
const MAX_INIT_ATTEMPTS = 10;
const INIT_CHECK_INTERVAL = 500; // ms

// Get mixpanel from window (loaded via script tag)
const getMixpanel = () => window.mixpanel;

/**
 * Check if Mixpanel is ready and process queued events
 */
const checkMixpanelReady = () => {
  const mixpanel = getMixpanel();
  
  if (mixpanel && typeof mixpanel.track === 'function') {
    isInitialized = true;
    processEventQueue();
    return true;
  }
  
  initCheckAttempts++;
  if (initCheckAttempts < MAX_INIT_ATTEMPTS) {
    setTimeout(checkMixpanelReady, INIT_CHECK_INTERVAL);
  } else {
    console.warn('Mixpanel failed to initialize after', MAX_INIT_ATTEMPTS, 'attempts');
  }
  
  return false;
};

/**
 * Process queued events once Mixpanel is ready
 */
const processEventQueue = () => {
  const mixpanel = getMixpanel();
  if (!mixpanel || eventQueue.length === 0) return;
  
  console.log(`Processing ${eventQueue.length} queued analytics events`);
  
  while (eventQueue.length > 0) {
    const event = eventQueue.shift();
    try {
      if (event.type === 'track') {
        mixpanel.track(event.name, event.properties);
      } else if (event.type === 'identify') {
        mixpanel.identify(event.userId);
        if (event.properties) {
          mixpanel.people.set(event.properties);
        }
      }
    } catch (e) {
      console.error('Error processing queued event:', e);
    }
  }
};

// Start checking for Mixpanel on load
if (typeof window !== 'undefined') {
  setTimeout(checkMixpanelReady, INIT_CHECK_INTERVAL);
}

/**
 * Queue or send an event
 */
const queueOrSend = (eventType, eventName, properties = {}, userId = null) => {
  const mixpanel = getMixpanel();
  
  if (isInitialized && mixpanel) {
    try {
      if (eventType === 'track') {
        mixpanel.track(eventName, properties);
      } else if (eventType === 'identify') {
        mixpanel.identify(userId);
        if (properties) {
          mixpanel.people.set(properties);
        }
      }
    } catch (e) {
      console.error('Analytics error:', e);
    }
  } else {
    // Queue event for later processing
    eventQueue.push({
      type: eventType,
      name: eventName,
      properties,
      userId,
      timestamp: new Date().toISOString()
    });
    
    // Limit queue size to prevent memory issues
    if (eventQueue.length > 100) {
      eventQueue.shift(); // Remove oldest event
    }
  }
};

/**
 * Identify user after login - CRITICAL for user tracking
 */
export const identifyUser = (user) => {
  if (!user) return;

  const properties = {
    '$email': user.email,
    '$name': user.full_name || user.name,
    'role': user.role,
    'organization_id': user.organization_id,
    'payment_status': user.payment_status,
    'workspace_type': user.workspace_type,
    'created_at': user.created_at,
    'is_active': user.is_active
  };

  queueOrSend('identify', null, properties, user.id);
  
  // Track login event
  queueOrSend('track', 'User Login', {
    role: user.role,
    payment_status: user.payment_status,
    timestamp: new Date().toISOString()
  });
};

/**
 * Reset on logout
 */
export const resetUser = () => {
  const mixpanel = getMixpanel();
  if (!mixpanel) return;
  
  try {
    mixpanel.track('User Logout');
    mixpanel.reset();
  } catch (e) {
    console.error('Analytics reset error:', e);
  }
};

/**
 * Track generic event
 */
export const trackEvent = (eventName, properties = {}) => {
  queueOrSend('track', eventName, {
    ...properties,
    timestamp: new Date().toISOString()
  });
};

// ============================================
// BUSINESS EVENT TRACKERS
// ============================================

// --- EMAIL MARKETING ---
export const trackEmailSent = (data) => {
  trackEvent('Email Campaign Sent', {
    recipient_count: data.recipientCount,
    email_type: data.emailType,
    subject: data.subject,
    has_attachments: data.hasAttachments || false
  });
};

export const trackEmailOpened = (emailId) => {
  trackEvent('Email Opened', { email_id: emailId });
};

export const trackEmailClicked = (emailId, linkUrl) => {
  trackEvent('Email Link Clicked', { email_id: emailId, link_url: linkUrl });
};

// --- CONTACTS ---
export const trackContactCreated = (source = 'manual') => {
  trackEvent('Contact Created', { source });
};

export const trackContactsImported = (count, segmentName) => {
  trackEvent('Contacts Imported', {
    count,
    segment_name: segmentName
  });
};

export const trackContactDeleted = () => {
  trackEvent('Contact Deleted');
};

// --- MAILING LISTS ---
export const trackMailingListCreated = (listName) => {
  trackEvent('Mailing List Created', { list_name: listName });
};

export const trackContactsAddedToList = (listName, count) => {
  trackEvent('Contacts Added to List', {
    list_name: listName,
    contact_count: count
  });
};

// --- DRIP CAMPAIGNS ---
export const trackDripCampaignCreated = (campaignName, emailCount) => {
  trackEvent('Drip Campaign Created', {
    campaign_name: campaignName,
    email_count: emailCount
  });
};

export const trackDripCampaignActivated = (campaignName) => {
  trackEvent('Drip Campaign Activated', { campaign_name: campaignName });
};

// --- GTM PROJECTS ---
export const trackGTMProjectCreated = (data) => {
  trackEvent('GTM Project Created', {
    industry: data.industry,
    target_market: data.targetMarket,
    budget: data.budget
  });
};

export const trackGTMTaskCompleted = (taskType) => {
  trackEvent('GTM Task Completed', { task_type: taskType });
};

export const trackGTMContentGenerated = (contentType) => {
  trackEvent('GTM Content Generated', { content_type: contentType });
};

// --- PAYMENTS ---
export const trackPaymentInitiated = (amount, plan) => {
  trackEvent('Payment Initiated', { amount, plan });
};

export const trackPaymentCompleted = (amount, plan, paymentMethod) => {
  const mixpanel = getMixpanel();
  
  trackEvent('Payment Completed', { amount, plan, payment_method: paymentMethod });
  
  // Track revenue if Mixpanel is ready
  if (mixpanel && isInitialized) {
    try {
      mixpanel.people.track_charge(amount, {
        plan,
        payment_method: paymentMethod
      });
    } catch (e) {
      console.error('Error tracking charge:', e);
    }
  }
};

export const trackCouponApplied = (couponCode, discount) => {
  trackEvent('Coupon Applied', { coupon_code: couponCode, discount });
};

// --- FEATURE USAGE ---
export const trackFeatureUsed = (featureName, details = {}) => {
  trackEvent('Feature Used', {
    feature: featureName,
    ...details
  });
};

export const trackPageViewed = (pageName) => {
  trackEvent('Page Viewed', { page: pageName });
};

// --- WHATSAPP ---
export const trackWhatsAppMessageSent = (contactCount) => {
  trackEvent('WhatsApp Message Sent', { contact_count: contactCount });
};

// --- SOCIAL MEDIA ---
export const trackSocialPostScheduled = (platform) => {
  trackEvent('Social Post Scheduled', { platform });
};

// --- WEB FORMS ---
export const trackWebFormCreated = (formName) => {
  trackEvent('Web Form Created', { form_name: formName });
};

export const trackWebFormSubmission = (formName) => {
  trackEvent('Web Form Submission', { form_name: formName });
};

// --- WORKFLOWS ---
export const trackWorkflowCreated = (workflowName, triggerType) => {
  trackEvent('Workflow Created', {
    workflow_name: workflowName,
    trigger_type: triggerType
  });
};

export const trackWorkflowActivated = (workflowName) => {
  trackEvent('Workflow Activated', { workflow_name: workflowName });
};

// --- USER ACTIONS ---
export const trackSignup = (method = 'email') => {
  trackEvent('User Signup', { method });
};

export const trackInviteSent = (role) => {
  trackEvent('Team Invite Sent', { invited_role: role });
};

export const trackWorkspaceCreated = (workspaceType) => {
  trackEvent('Workspace Created', { workspace_type: workspaceType });
};

export const trackWorkspaceSwitched = () => {
  trackEvent('Workspace Switched');
};

// Export default object for convenience
export default {
  identifyUser,
  resetUser,
  trackEvent,
  trackEmailSent,
  trackContactCreated,
  trackContactsImported,
  trackMailingListCreated,
  trackContactsAddedToList,
  trackDripCampaignCreated,
  trackGTMProjectCreated,
  trackPaymentCompleted,
  trackFeatureUsed,
  trackPageViewed,
  trackSignup
};
